import * as React from "react";

import { Button } from "@replo/design-system/components/button/Button";
import { Empty } from "@replo/design-system/components/empty/Empty";
import { Tag } from "@replo/design-system/components/tag/Tag";
import { Tooltip } from "@replo/design-system/components/tooltip/Tooltip";
import { useCurrentWorkspaceContext } from "@v2/contexts/WorkspaceDashboardContext";
import { AddAnalyticsCustomDomainModal } from "@v2/features/settings/shop/modals/AddAnalyticsCustomDomainModal";
import {
  v2CustomDomainRouter,
  v2CustomDomainUtils,
} from "@v2/trpc/routes/v2CustomDomain";
import { Globe, RefreshCw, Trash2 } from "lucide-react";
import { truncateDomain } from "replo-utils/lib/url";

interface DomainData {
  domain: string;
  id: string;
  canBeDeleted: boolean;
  status: "verified" | "pending";
}

export const DomainsDisplay: React.FC<{
  projectId: string;
  shopDomainPrefix?: string | null;
}> = ({ projectId, shopDomainPrefix }) => {
  const { data: customDomainsData } =
    v2CustomDomainRouter.getByProjectId.useQuery({ projectId });
  const { workspace } = useCurrentWorkspaceContext();
  const [customDomainModalOpen, setCustomDomainModalOpen] =
    React.useState(false);

  const customDomains = customDomainsData?.customDomains ?? [];

  const domains: DomainData[] = [
    ...(shopDomainPrefix
      ? [
          {
            domain: `${shopDomainPrefix}.reploshops.com`,
            canBeDeleted: false,
            status: "verified" as const,
            id: "PLACEHOLDER_ID",
          },
        ]
      : []),
    ...customDomains.map((domain) => ({
      domain: domain.value,
      id: domain.id,
      canBeDeleted: true,
      status: domain.isPending ? ("pending" as const) : ("verified" as const),
    })),
  ];

  if (domains.length === 0) {
    return (
      <>
        <div className="flex w-full items-center justify-center py-12">
          <Empty
            icon={<Globe />}
            title="No domains configured"
            description="Add a custom domain to make your shop accessible at your own URL"
            size="base"
          >
            <Button
              variant="primary"
              size="base"
              onClick={() => {
                if (workspace) {
                  setCustomDomainModalOpen(true);
                }
              }}
            >
              Add Custom Domain
            </Button>
          </Empty>
        </div>
        {workspace && (
          <AddAnalyticsCustomDomainModal
            open={customDomainModalOpen}
            onOpenChange={setCustomDomainModalOpen}
            workspaceId={workspace.id}
            projectId={projectId}
          />
        )}
      </>
    );
  }

  return (
    <div className="flex flex-col gap-3 w-full">
      <div className="flex flex-col gap-2">
        {domains.map((domainData, index) => (
          <React.Fragment key={domainData.domain}>
            <DomainEntry
              index={index + 1}
              customDomain={domainData.domain}
              customDomainId={domainData.id}
              canBeDeleted={domainData.canBeDeleted}
              status={domainData.status}
              projectId={projectId}
            />
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

const DomainEntry = ({
  index,
  customDomain,
  customDomainId,
  canBeDeleted,
  status,
  projectId,
}: {
  index: number;
  customDomain: string;
  customDomainId: string;
  canBeDeleted: boolean;
  status: "verified" | "pending";
  projectId: string;
}) => {
  const { workspace } = useCurrentWorkspaceContext();

  const { mutate: deleteCustomDomain } =
    v2CustomDomainRouter.deleteCustomDomain.useMutation({
      onSuccess: () => {
        void v2CustomDomainUtils.getByProjectId.invalidate({
          projectId,
        });
      },
    });

  const {
    mutate: completePendingDomainSetup,
    isPending: isCompletingPendingDomainSetup,
  } = v2CustomDomainRouter.completePendingDomainSetup.useMutation({
    onSuccess: () => {
      void v2CustomDomainUtils.getByProjectId.invalidate({
        projectId,
      });
    },
  });

  const handleRefreshDomain = async () => {
    if (!workspace) {
      return;
    }

    completePendingDomainSetup({
      projectId,
      customDomainId,
      workspaceId: workspace.id,
    });
  };

  return (
    <div className="flex items-center">
      {/* NOTE (Cole, 2025-07-03): I set a static width here so numbers with multiple digits don't shift the layout */}
      <span className="text-sm font-medium text-muted w-6">{index}</span>
      <div className="flex items-center justify-between w-full bg-sheet border-0.5 border-border gap-3 p-3 rounded-lg">
        <span className="text-sm font-medium flex-1" title={customDomain}>
          {truncateDomain(customDomain)}
        </span>
        <div className="flex items-center gap-2">
          {status === "pending" && (
            <Tooltip content="Check if domain is ready" triggerAsChild>
              <Button
                icon
                variant="secondary"
                size="sm"
                onClick={() => void handleRefreshDomain()}
                loading={isCompletingPendingDomainSetup}
              >
                <RefreshCw />
              </Button>
            </Tooltip>
          )}
          {canBeDeleted && (
            <Tooltip content="Remove domain" triggerAsChild>
              <Button
                icon
                variant="secondary"
                size="sm"
                onClick={() => {
                  return deleteCustomDomain({
                    projectId,
                    customDomainId,
                  });
                }}
              >
                <Trash2 />
              </Button>
            </Tooltip>
          )}
          {status === "pending" ? (
            <Tag variant="info" size="base">
              Pending
            </Tag>
          ) : (
            <Tag variant="success" size="base">
              Verified
            </Tag>
          )}
        </div>
      </div>
    </div>
  );
};
